import React, { useEffect } from 'react';
import {
  Box,
  Fab,
  Button,
  Typography,
  Slide,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import useSessionContext from 'src/hooks/useSessionContext';
import HelpIcon from '@mui/icons-material/HelpOutlineRounded';
import { blueGrey, grey } from '@mui/material/colors';
import { useTheme } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import useWidgetContext from 'src/hooks/useWidgetContext';

const helpFabStyle = {
  position: 'fixed',
  bottom: (theme) => theme.spacing(2),
  right: (theme) => theme.spacing(3),
  paddingLeft: (theme) => theme.spacing(3),
  paddingRight: (theme) => theme.spacing(3),
  backgroundColor: (theme) => {
    return theme.palette.mode === 'light' ? grey[800] : blueGrey[200];
  },
  color: (theme) => {
    return theme.palette.mode === 'light' ? grey[100] : blueGrey[900];
  },
  '&:hover': {
    backgroundColor: (theme) => {
      return theme.palette.mode === 'light' ? grey[700] : blueGrey[300];
    }
  }
};

const closeBtnStyle = {
  position: 'absolute',
  top: '0',
  right: '20px',
  height: '36px',
  boxShadow: 'none !important',
  minWidth: 'unset',
  borderRadius: (theme) => theme.shape.borderRadius / 2,
  border: (theme) => {
    return `1px solid ${
      theme.palette.mode === 'light' ? grey[300] : blueGrey[700]
    }`;
  },
  color: (theme) => {
    return theme.palette.mode === 'light' ? grey[500] : blueGrey[300];
  },
  backgroundColor: (theme) => {
    return theme.palette.mode === 'light' ? grey[100] : blueGrey[800];
  },
  '&:hover': {
    backgroundColor: (theme) => {
      return theme.palette.mode === 'light' ? grey[200] : blueGrey[900];
    }
  }
};

export default function ZendeskLauncher() {
  const { session } = useSessionContext();
  const user = session?.user;
  const {
    state: { showLauncher, showMenu },
    dispatch
  } = useWidgetContext();
  const theme = useTheme();

  const handleOpenChat = () => {
    dispatch([
      { type: 'showLauncher', payload: false },
      { type: 'showMenu', payload: false }
    ]);
    window.zE('webWidget', 'show');
    window.zE('webWidget', 'open');
  };

  const handleOpenMenu = () => {
    dispatch({ type: 'showLauncher', payload: false });
    setTimeout(() => {
      dispatch({ type: 'showMenu', payload: true });
    }, 200);
  };

  const handleCloseMenu = () => {
    dispatch({ type: 'showMenu', payload: false });
    setTimeout(() => {
      dispatch({ type: 'showLauncher', payload: true });
    }, 200);
  };

  useEffect(() => {
    if (window.zE) {
      window.zE('webWidget:on', 'close', () => {
        window.zE('webWidget', 'hide');
        dispatch([
          { type: 'showLauncher', payload: true },
          { type: 'showMenu', payload: false }
        ]);
      });
      window.zE('webWidget', 'hide');
    }
  }, []);

  useEffect(() => {
    if (!user && !showLauncher && window.zE) {
      window.zE('webWidget', 'reset');
    }
  }, [showLauncher, user]);

  useEffect(() => {
    if (window.zE) {
      window.zE('webWidget', 'updateSettings', {
        webWidget: {
          color: {
            theme: theme.palette.mode === 'light' ? grey[800] : blueGrey[200]
          }
        }
      });
    }
  }, [theme.palette.mode]);

  useEffect(() => {
    if (window.zE) {
      if (user) {
        window.zE('webWidget', 'prefill', {
          name: {
            value: `${user.given_name} ${user.family_name}`,
            readOnly: true // optional
          },
          email: {
            value: user.email,
            readOnly: true // optional
          }
        });
        // set GA user id
        window.gtag('set', { user_id: user.id });
        window.gtag('set', 'user_properties', {
          bootstrapper_id: user.id
        });
      } else {
        window.zE('webWidget', 'reset');
      }
    }
  }, [user]);

  const messageUsMenuItem = () => {
    if (!user) return null;
    return (
      <ListItem
        sx={{
          paddingLeft: theme.spacing(5),
          paddingRight: theme.spacing(10)
        }}
        button
        onClick={handleOpenChat}
      >
        <ListItemIcon>
          <ChatIcon />
        </ListItemIcon>
        <ListItemText primary="Message Us" />
      </ListItem>
    );
  };

  // const myRequestMenuItem = () => {
  //   if (!user) return null;
  //   return (
  //     <>
  //       <ListItem
  //         sx={{
  //           paddingLeft: theme.spacing(5),
  //           paddingRight: theme.spacing(10)
  //         }}
  //         button
  //         component="a"
  //         href="https://z3n-bootstrapper.zendesk.com/hc/en-us"
  //         target="_blank"
  //         rel="noreferrer"
  //       >
  //         <ListItemIcon>
  //           <FindInPageIcon />
  //         </ListItemIcon>
  //         <ListItemText primary="My Request" />
  //       </ListItem>
  //       <Divider />
  //     </>
  //   );
  // };

  return (
    <>
      <Slide direction="up" in={showLauncher} mountOnEnter>
        <Fab variant="extended" onClick={handleOpenMenu} sx={helpFabStyle}>
          <HelpIcon />
          <Typography
            variant="body1"
            sx={{
              marginLeft: '6px',
              textTransform: 'initial'
            }}
          >
            Help
          </Typography>
        </Fab>
      </Slide>
      <Slide direction="up" in={showMenu} mountOnEnter>
        <Box
          sx={{
            position: 'fixed',
            bottom: '0',
            right: theme.spacing(3),
            paddingTop: '20px'
          }}
        >
          <Paper
            sx={{
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(1),
              borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
              backgroundColor:
                theme.palette.mode === 'light' ? 'white' : '#2d3c4c'
            }}
            elevation={10}
          >
            <Button sx={closeBtnStyle} onClick={handleCloseMenu}>
              <CloseIcon fontSize="small" />
            </Button>
            <List>
              {/* {myRequestMenuItem()} */}
              {messageUsMenuItem()}
              <ListItem
                sx={{
                  paddingLeft: theme.spacing(5),
                  paddingRight: theme.spacing(10)
                }}
                button
                component="a"
                href="https://z3n-bootstrapper.zendesk.com/hc/en-us"
                target="_blank"
                rel="noreferrer"
              >
                <ListItemIcon>
                  <FindInPageIcon />
                </ListItemIcon>
                <ListItemText primary="Help Center" />
              </ListItem>
            </List>
          </Paper>
        </Box>
      </Slide>
    </>
  );
}
